import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../services/api';

// Define types
export interface User {
  mobileNumber: string;
  role: string;
  userId: number;
  name: string | null;
  firstName?: string;
  lastName?: string;
  photoId?: string;
}

interface VerifyOtpResponse {
  status: boolean;
  message: string;
  data: {
    token: string;
    tokenType: string;
    role: string;
    mobileNumber: string;
    name: string | null;
    userId: number;
  };
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  tokenType: string | null;
  loading: boolean;
  error: string | null;
  otpSent: boolean;
  mobileNumber: string | null;
}

// Define initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  tokenType: null,
  loading: false,
  error: null,
  otpSent: false,
  mobileNumber: null,
};

// Async thunks
export const requestOtp = createAsyncThunk(
  'auth/requestOtp',
  async (mobileNumber: string, { rejectWithValue }) => {
    try {
      const response = await api.login(mobileNumber);
      // Cast the response to include expected properties
      const typedResponse = response as { message: string };
      return { mobileNumber, message: typedResponse.message, otpSent: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send OTP');
    }
  }
);

// Update the verifyOtp async thunk to properly type the response
export const verifyOtp = createAsyncThunk<
  VerifyOtpResponse,
  { mobileNumber: string; otp: string },
  { rejectValue: string }
>(
  'auth/verifyOtp',
  async ({ mobileNumber, otp }, { rejectWithValue }) => {
    try {
      const response = await api.verifyOtp(mobileNumber, otp);
      return response as VerifyOtpResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'OTP verification failed');
    }
  }
);

// Create slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.otpSent = false;
      state.mobileNumber = null;
      api.clearAuthToken();
      localStorage.removeItem('auth');
    },
    restoreAuth: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      api.setAuthToken(action.payload.token);
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Request OTP
    builder.addCase(requestOtp.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(requestOtp.fulfilled, (state, action) => {
      state.loading = false;
      state.otpSent = true;
      state.mobileNumber = action.payload.mobileNumber;
    });
    builder.addCase(requestOtp.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Verify OTP
    builder.addCase(verifyOtp.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(verifyOtp.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.isAuthenticated = true;
      state.token = action.payload.data.token;
      state.tokenType = action.payload.data.tokenType;
      state.user = {
        mobileNumber: action.payload.data.mobileNumber,
        role: action.payload.data.role,
        userId: action.payload.data.userId,
        name: action.payload.data.name,
        // Set default values for optional properties
        firstName: action.payload.data.name?.split(' ')[0] || undefined,
        lastName: action.payload.data.name?.split(' ').slice(1).join(' ') || undefined,
        photoId: undefined
      };
      state.otpSent = false;
      
      // Store authentication data in localStorage
      const authData = {
        token: action.payload.data.token,
        tokenType: action.payload.data.tokenType,
        user: state.user,
        isAuthenticated: true
      };
      localStorage.setItem('auth', JSON.stringify(authData));
      
      // Set the auth token for API calls
      api.setAuthToken(action.payload.data.token);
    });
    builder.addCase(verifyOtp.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { logout, restoreAuth, clearError } = authSlice.actions;
export default authSlice.reducer;
